import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import Select from "react-select";
import { API_BASE_URL } from "../../../environment";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import TablePagination from "../../../components/pagination/TablePagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

const route = all_routes;

interface AiUsageLog {
  ai_usage_log_id: number;
  tenant_id: number;
  ai_provider_id: number;
  ai_provider_model_id: number;
  request_type: string;
  total_token_count: number;
  status: string;
  created_datetime: string;
  metadata: any;
  tenant: Tenant;
}

interface AiProvider {
  ai_provider_id: number;
  ai_provider_key: string;
  ai_provider_display_name: string;
}

interface Tenant {
  tenant_id: number;
  tenant_name: string;
}

const AiUsageLogs = () => {
  const location = useLocation();

  const [provider, setProvider] = useState<any>(null);
  const [logs, setLogs] = useState<AiUsageLog[]>([]);
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [tenant, setTenant] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantPage, setTenantPage] = useState(1);
  const [hasMoreTenants, setHasMoreTenants] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");

  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set(["dateRange", "tenant", "provider", "status"]));
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        filterDropdownRef.current && 
        !filterDropdownRef.current.contains(target) &&
        !target.closest('.daterangepicker') &&
        !target.closest('.react-select__menu')
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTenants(1, tenantSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [tenantSearchTerm]);

  useEffect(() => {
    fetchLogs(currentPage, pageSize, provider?.value, tenant?.value, status?.value, dateRange);
  }, [currentPage, provider, tenant, status, dateRange]);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Basic ${localStorage.getItem("authCred")}`,
  });

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-provider/?is_active=true`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const fetchTenants = async (page: number, search: string = "") => {
    if (loadingTenants || (!hasMoreTenants && page !== 1)) return;
    setLoadingTenants(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/?page=${page}&page_size=50${search ? `&tenant=${search}` : ''}`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setTenants((prev) => (page === 1 ? data.results : [...prev, ...data.results]));
        setHasMoreTenants(data.next !== null);
        setTenantPage(page);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleTenantScrollToBottom = () => {
    if (hasMoreTenants && !loadingTenants) {
      fetchTenants(tenantPage + 1, tenantSearchTerm);
    }
  };

  const fetchLogs = async (page: number, size: number, providerId: any, tenantId: any, statusValue: any, range: [Date | null, Date | null]) => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/ai-usage-log/?page=${page}&page_size=${size}`;
      if (providerId && providerId !== "all") {
        url += `&ai_provider_id=${providerId}`;
      }
      if (tenantId && tenantId !== "all") {
        url += `&tenant_id=${tenantId}`;
      }
      if (statusValue && statusValue !== "all") {
        url += `&status=${statusValue}`;
      }
      if (range[0] && range[1]) {
        const startDate = range[0].toISOString().split("T")[0];
        const endDate = range[1].toISOString().split("T")[0];

        const start = new Date(startDate);
        const end = new Date(endDate);
        const dayDifference = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

        if (startDate === endDate || dayDifference <= 1) {
          url += `&created_datetime=${endDate}`;
        } else {
          url += `&created_datetime__gte=${startDate}&created_datetime__lte=${endDate}`;
        }
      } else if (range[0]) {
        url += `&created_datetime__gte=${range[0].toISOString().split("T")[0]}`;
      } else if (range[1]) {
        url += `&created_datetime__lte=${range[1].toISOString().split("T")[0]}`;
      }

      const response = await fetch(url, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.results || []);
        setTotalCount(data.total_count || 0);
        setFailedCount(data.failed_count || 0);
        setSuccessCount(data.success_count || 0);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const tenantOptions = [
    { value: "all", label: "All Tenants" },
    ...tenants.map(t => ({ value: t.tenant_id, label: t.tenant_name }))
  ];

  const formatTenantOptionLabel = (option: any) => {
    if (option.value === "all") return option.label;
    return (
      <div className="d-flex justify-content-between align-items-center">
        <span>{option.label}</span>
        <span className="badge bg-primary ms-2">{option.value}</span>
      </div>
    );
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "SUCCESS", label: "Success" },
    { value: "FAILED", label: "Failed" }
  ];

  const getActiveFilterCount = () => {
    let count = 0;
    if (tenant && tenant.value !== "all") count++;
    if (provider && provider.value !== "all") count++;
    if (status && status.value !== "all") count++;
    if (dateRange[0] || dateRange[1]) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const resetFilters = () => {
    setTenant(null);
    setProvider(null);
    setStatus(null);
    setDateRange([null, null]);
    setFilterOpen(false);
    setCurrentPage(1);
  };

  const toggleFilterExpansion = (filterKey: string) => {
    const newExpandedFilters = new Set(expandedFilters);
    if (newExpandedFilters.has(filterKey)) {
      newExpandedFilters.delete(filterKey);
    } else {
      newExpandedFilters.add(filterKey);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        new Date(new Date().setDate(new Date().getDate() - 30)),
        new Date(),
      ],
      "Last 7 Days": [
        new Date(new Date().setDate(new Date().getDate() - 7)),
        new Date(),
      ],
      "Last Month": [
        new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
        new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      ],
      "This Month": [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(),
      ],
      Today: [new Date(), new Date()],
      Yesterday: [
        new Date(new Date().setDate(new Date().getDate() - 1)),
        new Date(new Date().setDate(new Date().getDate() - 1)),
      ],
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    timePicker: false,
    autoUpdateInput: false,
    locale: { cancelLabel: 'Clear' }
  };

  const handleApplyDateRange = (event: any, picker: any) => {
    setDateRange([picker.startDate.toDate(), picker.endDate.toDate()]);
    setCurrentPage(1);
  };

  const handleCancelDateRange = (event: any, picker: any) => {
    setDateRange([null, null]);
    picker.element.val("");
    setCurrentPage(1);
  };

  const providerOptions = [
    { value: "all", label: "All Providers" },
    ...providers.map(p => ({ value: p.ai_provider_id, label: p.ai_provider_display_name }))
  ];

  const getProviderName = (providerId: number) => {
    const p = providers.find(p => p.ai_provider_id === providerId);
    return p ? p.ai_provider_display_name : `Provider #${providerId}`;
  };

  const getProviderKey = (providerId: number) => {
    const p = providers.find(p => p.ai_provider_id === providerId);
    return p ? p.ai_provider_key : "";
  };

  const formatTitleCase = (str: string) => {
    if (!str) return str;
    return str
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-4">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-sm-8 text-sm-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            <div className="card">
              <div className="card-body">
                <div className="settings-sidebar">
                  <h4 className="fw-semibold mb-3">AI Configuration</h4>
                  <div className="list-group list-group-flush settings-sidebar">
                    <Link
                      to={route.aiConfigurationCrmLevel}
                      className={`fw-medium ${location.pathname === route.aiConfigurationCrmLevel
                        ? "active"
                        : ""
                        }`}
                    >
                      CRM Level Configuration
                    </Link>
                    <Link
                      to={route.aiConfigurationTenantLevel}
                      className={`fw-medium ${location.pathname === route.aiConfigurationTenantLevel
                        ? "active"
                        : ""
                        }`}
                    >
                      Tenant Level Configuration
                    </Link>
                    <Link
                      to={route.aiConfigurationUsageLogs}
                      className={`fw-medium ${location.pathname === route.aiConfigurationUsageLogs
                        ? "active"
                        : ""
                        }`}
                    >
                      AI Usage Logs
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h4 className="fs-20 mb-0">AI Usage Logs</h4>
                  <div className="d-flex gap-2 flex-wrap">
                    <div className="position-relative" ref={filterDropdownRef}>
                      <button
                        type="button"
                        className="btn btn-outline-light shadow dropdown-toggle position-relative"
                        onClick={() => setFilterOpen(!filterOpen)}
                      >
                        <img
                          src="/assets/img/icons/filter.svg"
                          alt="Filter"
                          className="me-1"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <span className="me-2 text-dark">Filter</span>
                        {activeFilterCount > 0 && (
                          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
                            <span className="visually-hidden">New alerts</span>
                          </span>
                        )}
                      </button>

                      {filterOpen && (
                        <div
                          className="dropdown-menu show"
                          style={{
                            width: "350px",
                            maxHeight: "400px",
                            zIndex: 1000,
                            position: "absolute",
                            top: "100%",
                            right: "0",
                            left: "auto",
                            marginTop: "0.125rem",
                            backgroundColor: "white",
                            border: "1px solid rgba(0,0,0,.15)",
                            borderRadius: "0.375rem",
                            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div className="p-3 pb-0">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">
                                <img
                                  src="/assets/img/icons/filter.svg"
                                  alt="Filter"
                                  className="me-1"
                                  style={{ width: "16px", height: "16px" }}
                                />
                                Filters
                              </h6>
                            </div>
                          </div>

                          <div className="px-3" style={{ maxHeight: "300px", overflowY: "auto", flex: 1 }}>
                            <div className="space-y-2">

                              {/* Date Range Filter */}
                              <div className="border rounded">
                                <button
                                  type="button"
                                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                                  onClick={() => toggleFilterExpansion("dateRange")}
                                >
                                  <span className="small text-dark">
                                    <strong>Date Range</strong>
                                  </span>
                                  <div className="d-flex align-items-center">
                                    {dateRange[0] && dateRange[1] && (
                                      <span className="small text-muted me-2">
                                        {`${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`}
                                      </span>
                                    )}
                                    <i className={`ti ti-chevron-${expandedFilters.has("dateRange") ? "up" : "down"} small text-dark`}></i>
                                  </div>
                                </button>
                                {expandedFilters.has("dateRange") && (
                                  <div className="p-2 border-top">
                                    <DateRangePicker
                                      key={dateRange[0] ? "set" : "empty"}
                                      initialSettings={initialSettings}
                                      onApply={handleApplyDateRange}
                                      onCancel={handleCancelDateRange}
                                    >
                                      <input
                                        className="form-control"
                                        type="text"
                                        placeholder="Select Date Range"
                                        value={
                                          dateRange[0] && dateRange[1]
                                            ? `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`
                                            : ""
                                        }
                                        readOnly
                                      />
                                    </DateRangePicker>
                                  </div>
                                )}
                              </div>

                              {/* Tenant Filter */}
                              <div className="border rounded mt-2">
                                <button
                                  type="button"
                                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                                  onClick={() => toggleFilterExpansion("tenant")}
                                >
                                  <span className="small text-dark">
                                    <strong>Tenant</strong>
                                  </span>
                                  <div className="d-flex align-items-center">
                                    {tenant && tenant.value !== "all" && (
                                      <span className="small text-muted me-2" title={tenant.label}>
                                        {tenant.label.length > 15 ? tenant.label.substring(0, 15) + "..." : tenant.label}
                                      </span>
                                    )}
                                    <i className={`ti ti-chevron-${expandedFilters.has("tenant") ? "up" : "down"} small text-dark`}></i>
                                  </div>
                                </button>
                                {expandedFilters.has("tenant") && (
                                  <div className="p-2 border-top">
                                    <Select
                                      className="select"
                                      classNamePrefix="react-select"
                                      options={tenantOptions}
                                      formatOptionLabel={formatTenantOptionLabel}
                                      placeholder="Search Tenant Name or ID"
                                      value={tenant}
                                      onChange={(val) => {
                                        setTenant(val);
                                        setCurrentPage(1);
                                      }}
                                      onInputChange={(val, actionMeta) => {
                                        if (actionMeta.action === 'input-change') {
                                          setTenantSearchTerm(val);
                                        }
                                      }}
                                      onMenuScrollToBottom={handleTenantScrollToBottom}
                                      isLoading={loadingTenants}
                                      loadingMessage={() => "Loading tenants..."}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Provider Filter */}
                              <div className="border rounded mt-2">
                                <button
                                  type="button"
                                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                                  onClick={() => toggleFilterExpansion("provider")}
                                >
                                  <span className="small text-dark">
                                    <strong>Provider</strong>
                                  </span>
                                  <div className="d-flex align-items-center">
                                    {provider && provider.value !== "all" && (
                                      <span className="small text-muted me-2" title={provider.label}>
                                        {provider.label.length > 15 ? provider.label.substring(0, 15) + "..." : provider.label}
                                      </span>
                                    )}
                                    <i className={`ti ti-chevron-${expandedFilters.has("provider") ? "up" : "down"} small text-dark`}></i>
                                  </div>
                                </button>
                                {expandedFilters.has("provider") && (
                                  <div className="p-2 border-top">
                                    <Select
                                      className="select"
                                      classNamePrefix="react-select"
                                      options={providerOptions}
                                      placeholder="All Providers"
                                      value={provider}
                                      onChange={(val) => {
                                        setProvider(val);
                                        setCurrentPage(1);
                                      }}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Status Filter */}
                              <div className="border rounded mt-2">
                                <button
                                  type="button"
                                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                                  onClick={() => toggleFilterExpansion("status")}
                                >
                                  <span className="small text-dark">
                                    <strong>Status</strong>
                                  </span>
                                  <div className="d-flex align-items-center">
                                    {status && status.value !== "all" && (
                                      <span className="small text-muted me-2">
                                        {status.label}
                                      </span>
                                    )}
                                    <i className={`ti ti-chevron-${expandedFilters.has("status") ? "up" : "down"} small text-dark`}></i>
                                  </div>
                                </button>
                                {expandedFilters.has("status") && (
                                  <div className="p-2 border-top">
                                    <Select
                                      className="select"
                                      classNamePrefix="react-select"
                                      options={statusOptions}
                                      placeholder="All Status"
                                      value={status}
                                      onChange={(val) => {
                                        setStatus(val);
                                        setCurrentPage(1);
                                      }}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <br />

                          {activeFilterCount > 0 && (
                            <div className="p-3 pt-2 border-top" style={{ backgroundColor: "white" }}>
                              <div className="d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm flex-fill"
                                  onClick={resetFilters}
                                >
                                  <i className="ti ti-refresh me-1"></i>
                                  Reset
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row gy-3 mb-4">
                  <div className="col-md-4">
                    <div className="card bg-light-info mb-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="text-muted mb-2">Total Requests</h6>
                            <h3 className="mb-0">{totalCount}</h3>
                          </div>
                          <div className="avatar avatar-lg bg-info">
                            <i className="ti ti-api" style={{ fontSize: "24px" }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light-success mb-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="text-muted mb-2">Successful</h6>
                            <h3 className="mb-0">{successCount}</h3>
                          </div>
                          <div className="avatar avatar-lg bg-success">
                            <i className="ti ti-check" style={{ fontSize: "24px" }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light-danger mb-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="text-muted mb-2">Failed</h6>
                            <h3 className="mb-0">{failedCount}</h3>
                          </div>
                          <div className="avatar avatar-lg bg-danger">
                            <i className="ti ti-x" style={{ fontSize: "24px" }}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Tenant Name</th>
                        <th>Provider</th>
                        <th className="text-center">Tokens Used</th>
                        <th>Status</th>
                        <th>Request Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4">
                            Loading logs...
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">
                            No logs found.
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.ai_usage_log_id}>
                            <td className="text-nowrap">{formatCustomDateTime(log.created_datetime)}</td>
                            <td className="text-nowrap">{log.tenant?.tenant_name || "N/A"} ({log.tenant?.tenant_id}) </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i
                                  className={`ti ${getProviderKey(log.ai_provider_id).includes("OPEN_AI")
                                    ? "ti-brain"
                                    : "ti-sparkles"
                                    } me-2 text-primary`}
                                ></i>
                                <span className="fw-medium text-dark">{getProviderName(log.ai_provider_id)}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary">
                                {log.total_token_count}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${log.status === "SUCCESS"
                                  ? "bg-soft-success text-success"
                                  : "bg-soft-danger text-danger"
                                  }`}
                              >
                                {formatTitleCase(log.status)}
                              </span>
                            </td>
                            <td>{formatTitleCase(log.request_type)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!loading && logs.length > 0 && (
                    <TablePagination
                      currentPage={currentPage}
                      pageSize={pageSize}
                      totalItems={totalCount}
                      filteredItems={totalCount}
                      isFiltered={
                        !!(provider && provider.value !== "all") ||
                        !!(tenant && tenant.value !== "all") ||
                        !!(status && status.value !== "all") ||
                        !!(dateRange[0] || dateRange[1])
                      }
                      onPageChange={(page: number) => setCurrentPage(page)}
                      onPageSizeChange={() => { }}
                      entityName="Logs"
                      loading={loading}
                    />
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiUsageLogs;
